// src/App.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { ethers } from 'ethers';
import WalletConnect from './components/WalletConnect';
import AuthSection from './components/AuthSection';
import AuthModal from './components/AuthModal';
import StakingPanel from './components/StakingPanel';
import EarningsPanel from './components/EarningsPanel';
import WalletSetupModal from './components/WalletSetupModal';
import Toast from './components/Toast';
import AnimatedBackground from './components/AnimatedBackground';
import StakingReactivationCard from './components/StakingReactivationCard';
import { stakingAbi } from './staking.abi';
import { ERC20Abi } from './erc20Abi';
import { Profile, StakingActivationStatus, Transaction } from './types';
import { 
  API_BASE, 
  TOKEN_ADDRESS, 
  STAKING_V1_ADDRESS, 
  STAKING_V2_ADDRESS,
  BSC_PARAMS,
  DECIMALS
} from './utils/constants';
import { toWei, weiToStr, clampWei, signWithTimeout, ZERO } from './utils/helpers';

interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

type ActiveTab = 'overview' | 'staking' | 'earnings';

const friendlyApiError = (error: unknown, fallback: string) => {
  if (error === 'invalid_ar_wallet_format') {
    return 'Invalid Arweave address format. Use the exact 43-character, case-sensitive wallet address.';
  }
  return typeof error === 'string' && error ? error : fallback;
};

function App() {
  // Web3 state
  const [ethProvider, setEthProvider] = useState<any>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  
  const ethersProvider = useMemo(() => {
    if (!ethProvider) return null;
    return new ethers.providers.Web3Provider(ethProvider);
  }, [ethProvider]);

  // Staking state
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("");
  const [tokenBalance, setTokenBalance] = useState("0");
  const [tokenBalanceWei, setTokenBalanceWei] = useState(ZERO);
  const [stakedV1, setStakedV1] = useState("0");
  const [stakedV1Wei, setStakedV1Wei] = useState(ZERO);
  const [cooldownV1, setCooldownV1] = useState("");
  const [stakedV2, setStakedV2] = useState("0");
  const [stakedV2Wei, setStakedV2Wei] = useState(ZERO);
  const [cooldownV2, setCooldownV2] = useState("");
  const [allowanceV2, setAllowanceV2] = useState("0");
  const [allowanceV2Wei, setAllowanceV2Wei] = useState(ZERO);

  // Auth state
  const [jwt, setJwt] = useState<string | null>(() => localStorage.getItem("jwt"));
  const [profile, setProfile] = useState<Profile>({});
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stakingActivation, setStakingActivation] = useState<StakingActivationStatus | null>(null);
  const [authStatus, setAuthStatus] = useState("");
  

  // Loading states
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isStaking, setIsStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [isLoadingActivation, setIsLoadingActivation] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  // Modal state
  const [showWalletSetup, setShowWalletSetup] = useState(false);

  // Toast state
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');

  const allRewardWalletsPresent = Boolean(profile.xch_wallet) && Boolean(profile.fil_wallet) && Boolean(profile.ar_wallet);

  // Toast utilities
  const addToast = (toast: Omit<ToastMessage, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newToast: ToastMessage = { ...toast, id };
    setToasts(prev => [...prev, newToast]);
    
    setTimeout(() => {
      removeToast(id);
    }, toast.duration || 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showSuccessToast = (title: string, message: string) => {
    addToast({ type: 'success', title, message });
  };

  const showErrorToast = (title: string, message: string) => {
    addToast({ type: 'error', title, message, duration: 7000 });
  };

  const showWarningToast = (title: string, message: string) => {
    addToast({ type: 'warning', title, message });
  };

  const showInfoToast = (title: string, message: string) => {
    addToast({ type: 'info', title, message });
  };

  const loadTransactions = async (token: string) => {
    try {
      const r = await fetch(`${API_BASE}/users/me/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || r.statusText);
      setTransactions(data as Transaction[]);
    } catch (e: any) {
      showErrorToast("Transactions Error", e?.message || "Failed to load transactions");
    }
  };

  const loadStakingActivation = async (token: string) => {
    try {
      setIsLoadingActivation(true);
      const response = await fetch(`${API_BASE}/staking/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || response.statusText);
      setStakingActivation(data as StakingActivationStatus);
    } catch (error: any) {
      showErrorToast('Activation Status Error', error?.message || 'Failed to load staking activation status');
    } finally {
      setIsLoadingActivation(false);
    }
  };

  const reactivateStaking = async () => {
    if (!jwt || isReactivating) return;
    try {
      setIsReactivating(true);
      const response = await fetch(`${API_BASE}/staking/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || response.statusText);
      setStakingActivation(data as StakingActivationStatus);
      showSuccessToast(
        data.action === 'reactivation' ? 'Staking Reactivated' : 'Staking Extended',
        data.action === 'reactivation'
          ? 'Your staking is active again. Rewards resume from now.'
          : 'Your staking has been extended for the next six-month period.'
      );
    } catch (error: any) {
      showErrorToast('Activation Failed', error?.message || 'Could not update staking activation');
    } finally {
      setIsReactivating(false);
    }
  };

  // Check if already connected on mount
  useEffect(() => {
    const checkConnection = async () => {
      const eth = (window as any).ethereum;
      if (!eth) {
        setIsInitializing(false);
        showWarningToast('MetaMask Required', 'Please install MetaMask browser extension to use this application.');
        return;
      }
      
      setEthProvider(eth);
      
      try {
        const accounts = await eth.request({ method: 'eth_accounts' });
        if (accounts && accounts.length > 0) {
          const provider = new ethers.providers.Web3Provider(eth);
          const s = provider.getSigner();
          const addr = await s.getAddress();
          setSigner(s);
          setAccount(addr);
          await refreshAll(addr, s);
          if (jwt) loadMe(jwt);
          showSuccessToast('Wallet Connected', 'Your wallet is already connected and ready to use.');
        }
      } catch (error) {
        console.debug("Initial connection check error:", error);
        showErrorToast('Connection Error', 'Failed to check existing wallet connection.');
      } finally {
        setIsInitializing(false);
      }
    };

    checkConnection();
  }, []);

  // Initialize window.ethereum listeners
  useEffect(() => {
    const eth = (window as any).ethereum;
    if (!eth) return;

    const handleAccountsChanged = async (accounts: string[]) => {
      if (!ethersProvider) return;
      if (accounts.length > 0) {
        const s = ethersProvider.getSigner();
        const addr = await s.getAddress();
        setSigner(s);
        setAccount(addr);
        await refreshAll(addr, s);
        if (jwt) loadMe(jwt);
        showInfoToast('Account Changed', 'Wallet account has been switched successfully.');
      } else {
        setAccount(null);
        setSigner(null);
        setProfile({});
        setStakingActivation(null);
        showWarningToast('Wallet Disconnected', 'Your wallet has been disconnected.');
      }
    };

    const handleChainChanged = () => {
      showInfoToast('Network Changed', 'Blockchain network changed. Refreshing application...');
      setTimeout(() => window.location.reload(), 1500);
    };

    eth.on?.("accountsChanged", handleAccountsChanged);
    eth.on?.("chainChanged", handleChainChanged);

    return () => {
      eth.removeListener?.("accountsChanged", handleAccountsChanged);
      eth.removeListener?.("chainChanged", handleChainChanged);
    };
  }, [jwt, ethersProvider]);

  // Handle unauthorized
const handleUnauthorized = () => {
  setJwt(null);
  localStorage.removeItem("jwt");
  setProfile({});
  setTransactions([]);
  setStakingActivation(null);
  setAuthStatus("❌ Unauthorized - please sign in again.");
  showErrorToast("Session Expired", "Your session has expired. Please log in again.");
};

  // Ensure wallet is ready
  const ensureWalletReady = async (): Promise<{ addr: string; s: ethers.Signer }> => {
    if (!ethProvider) throw new Error("MetaMask is not available in this browser.");
    if (!ethersProvider) throw new Error("Ethers provider is not available.");

    let accounts: string[] = [];
    try {
      accounts = await ethProvider.request({ method: "eth_requestAccounts" });
    } catch (e: any) {
      if (e?.code === -32002) {
        throw new Error("MetaMask request is already pending. Please click the extension icon and complete the request.");
      }
      if (e?.code === 4001) {
        throw new Error("Account access request was rejected.");
      }
      throw e;
    }
    if (!accounts || accounts.length === 0) throw new Error("No accounts found in MetaMask.");

    // Switch to BSC
    try {
      const currentChain = await ethProvider.request({ method: "eth_chainId" });
      if (currentChain?.toLowerCase() !== BSC_PARAMS.chainId.toLowerCase()) {
        try {
          await ethProvider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: BSC_PARAMS.chainId }],
          });
          showInfoToast('Network Switched', 'Successfully switched to BSC network.');
        } catch (err: any) {
          if (err?.code === 4902) {
            await ethProvider.request({ method: "wallet_addEthereumChain", params: [BSC_PARAMS] });
            showInfoToast('Network Added', 'BSC network has been added to your wallet.');
          } else if (err?.code === 4001) {
            throw new Error("Network switch to BSC was rejected.");
          } else {
            throw err;
          }
        }
      }
    } catch (e) {
      console.debug("Chain switch warning:", e);
      showWarningToast('Network Warning', 'Could not automatically switch to BSC network. Please switch manually.');
    }

    const s = ethersProvider.getSigner();
    const addr = await s.getAddress();
    setSigner(s);
    setAccount(addr);
    return { addr, s };
  };

  // Refresh all balances
  const refreshAll = async (address: string, s: ethers.Signer) => {
    try {
      const token = new ethers.Contract(TOKEN_ADDRESS, ERC20Abi, s);
      const stakingV1 = new ethers.Contract(STAKING_V1_ADDRESS, stakingAbi, s);
      const stakingV2 = new ethers.Contract(STAKING_V2_ADDRESS, stakingAbi, s);

      const bal: ethers.BigNumber = await token.balanceOf(address);
      setTokenBalanceWei(bal);
      setTokenBalance(weiToStr(bal));

      const infoV1 = await stakingV1.userInfo(address);
      const stV1Wei: ethers.BigNumber = infoV1.amount ?? ZERO;
      setStakedV1Wei(stV1Wei);
      setStakedV1(weiToStr(stV1Wei));
      if (infoV1.cooldownPeriodEnd && !infoV1.cooldownPeriodEnd.isZero()) {
        const date = new Date(infoV1.cooldownPeriodEnd.toNumber() * 1000);
        setCooldownV1(date.toLocaleString());
      } else {
        setCooldownV1("");
      }

      const infoV2 = await stakingV2.userInfo(address);
      const stV2Wei: ethers.BigNumber = infoV2.amount ?? ZERO;
      setStakedV2Wei(stV2Wei);
      setStakedV2(weiToStr(stV2Wei));
      if (infoV2.cooldownPeriodEnd && !infoV2.cooldownPeriodEnd.isZero()) {
        const date = new Date(infoV2.cooldownPeriodEnd.toNumber() * 1000);
        setCooldownV2(date.toLocaleString());
      } else {
        setCooldownV2("");
      }

      const allwV2: ethers.BigNumber = await token.allowance(address, STAKING_V2_ADDRESS);
      setAllowanceV2Wei(allwV2);
      setAllowanceV2(weiToStr(allwV2));
    } catch (err) {
      console.debug("Refresh error:", err);
      showErrorToast('Balance Refresh Error', 'Failed to refresh account balances. Please try again.');
    }
  };

  // Connect wallet
  const connectWallet = async () => {
    try {
      setIsConnecting(true);
      showInfoToast('Connecting...', 'Please approve the connection in your wallet.');
      const { addr, s } = await ensureWalletReady();
      await refreshAll(addr, s);
      setStatus("✅ Connected successfully!");
      showSuccessToast('Wallet Connected', `Successfully connected to ${addr.slice(0, 6)}...${addr.slice(-4)}`);
    } catch (err: any) {
      const errorMessage = err?.message || String(err);
      setStatus("❌ " + errorMessage);
      showErrorToast('Connection Failed', errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  // Load user profile
  const loadMe = async (token: string) => {
    try {
      const r = await fetch(`${API_BASE}/users/me`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = await r.json();
if (!r.ok) {
  if (r.status === 401) {
    handleUnauthorized();
    return;
  }
  const errorMsg = friendlyApiError(data?.error, r.statusText);
  setAuthStatus("⚠️ Profile load error: " + errorMsg);
  showErrorToast('Profile Error', errorMsg);
  return;
}
      setProfile(data as Profile);

      await loadTransactions(token);
      await loadStakingActivation(token);

      const needXch = !data.xch_wallet;
      const needFil = !data.fil_wallet;
      const needAr = !data.ar_wallet;
      setShowWalletSetup(needXch || needFil || needAr);
      setAuthStatus("👤 Profile loaded successfully.");
      showSuccessToast('Profile Loaded', 'User profile has been loaded successfully.');
      
      if (needXch || needFil || needAr) {
        showWarningToast('Wallet Setup Required', 'Please complete wallet setup to enable staking features.');
      }
    } catch (e: any) {
      const errorMsg = e?.message || 'Unknown error occurred';
      setAuthStatus("❌ Load profile error: " + errorMsg);
      showErrorToast('Profile Load Failed', errorMsg);
    }
  };

  // Login to backend
  const loginBackend = async () => {
    if (isLoggingIn) return;
    try {
      setIsLoggingIn(true);
      setAuthStatus("🔐 Preparing MetaMask...");
      showInfoToast('Authentication', 'Preparing MetaMask for authentication...');
      
      const { addr, s } = await ensureWalletReady();
      await refreshAll(addr, s);

      setAuthStatus("✍️ Fetching challenge...");
      showInfoToast('Getting Challenge', 'Requesting authentication challenge from server...');
      
      const r1 = await fetch(`${API_BASE}/auth/challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: addr }),
      });
      const challenge = await r1.json();
      if (!r1.ok) {
        const errorMsg = JSON.stringify(challenge);
        setAuthStatus("❌ Challenge error: " + errorMsg);
        showErrorToast('Challenge Failed', errorMsg);
        return;
      }
      const message: string = challenge.message;

      setAuthStatus("🖋️ Signing in MetaMask...");
      showInfoToast('Sign Message', 'Please sign the message in your MetaMask wallet...');
      
      let signature: string;
      try {
        signature = await signWithTimeout<string>(
          ethProvider.request({ method: "personal_sign", params: [message, addr] }),
          45000
        );
      } catch (e: any) {
        if (e?.code === -32002) {
          const pendingMsg = "Request already pending in MetaMask.";
          setAuthStatus("🟡 " + pendingMsg);
          showWarningToast('Pending Request', pendingMsg);
          return;
        }
        if (e?.code === 4001) {
          const rejectedMsg = "Signature was rejected.";
          setAuthStatus("❌ " + rejectedMsg);
          showErrorToast('Signature Rejected', rejectedMsg);
          return;
        }
        const { s } = await ensureWalletReady();
        signature = await signWithTimeout(s.signMessage(message), 45000);
      }

      setAuthStatus("🔍 Verifying signature...");
      showInfoToast('Verifying', 'Verifying your signature with the server...');
      
      const r2 = await fetch(`${API_BASE}/auth/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: addr, signature, message }),
      });
      const verified = await r2.json();
if (!r2.ok) {
  if (r2.status === 401) {
    handleUnauthorized();
    return;
  }
  const errorMsg = verified?.error || r2.statusText;
  setAuthStatus("❌ Verify error: " + errorMsg);
  showErrorToast('Verification Failed', errorMsg);
  return;
}

      const token = verified.jwt as string;
      setJwt(token);
      localStorage.setItem("jwt", token);
      setAuthStatus("✅ Logged in. Loading profile...");
      showSuccessToast('Login Successful', 'You have been successfully authenticated.');
      await loadMe(token);
    } catch (e: any) {
      const errorMsg = e?.message || String(e);
      setAuthStatus("❌ Login error: " + errorMsg);
      showErrorToast('Login Failed', errorMsg);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Save profile
  const saveMe = async () => {
    if (!jwt) {
      showWarningToast('Authentication Required', 'Please log in to the backend first.');
      return setAuthStatus("Please log in to the backend first.");
    }
    try {
      setIsSaving(true);
      showInfoToast('Saving Profile', 'Updating your profile information...');
      
      const r = await fetch(`${API_BASE}/users/me`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${jwt}` 
        },
        body: JSON.stringify({
          email: profile.email || null,
          xch_wallet: profile.xch_wallet || null,
          fil_wallet: profile.fil_wallet || null,
          ar_wallet: profile.ar_wallet || null,
        }),
      });
      const data = await r.json();
if (!r.ok) {
  if (r.status === 401) {
    handleUnauthorized();
    return;
  }
  const errorMsg = friendlyApiError(data?.error, r.statusText);
  setAuthStatus("❌ Save error: " + errorMsg);
  showErrorToast('Save Failed', errorMsg);
  return;
}
      setAuthStatus("💾 Profile saved successfully.");
      showSuccessToast('Profile Saved', 'Your profile has been updated successfully.');
      await loadMe(jwt);
    } catch (e: any) {
      const errorMsg = e?.message || 'Unknown error occurred';
      setAuthStatus("❌ Save error: " + errorMsg);
      showErrorToast('Save Error', errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  // Logout
  const logoutBackend = () => {
    setJwt(null);
    localStorage.removeItem("jwt");
    setProfile({});
    setTransactions([]);
    setStakingActivation(null);
    setAuthStatus("👋 Logged out successfully.");
    showInfoToast('Logged Out', 'You have been successfully logged out.');
  };

  // Get clamped amount for action
  const getClampedAmountFor = (action: "stakeV2" | "unstakeV1" | "unstakeV2"): ethers.BigNumber => {
    const inputWei = toWei(amount);
    if (action === "stakeV2") {
      return clampWei(inputWei, tokenBalanceWei);
    }
    if (action === "unstakeV1") {
      return clampWei(inputWei, stakedV1Wei);
    }
    return clampWei(inputWei, stakedV2Wei);
  };

  // Stake to V2
  const stakeOnV2 = async () => {
    if (!signer || !account) return;
    try {
      setIsStaking(true);
      const amtWei = getClampedAmountFor("stakeV2");
      if (amtWei.lte(ZERO)) {
        const errorMsg = "Amount must be > 0 and ≤ wallet balance.";
        setStatus("❌ " + errorMsg);
        showErrorToast('Invalid Amount', errorMsg);
        return;
      }

      const token = new ethers.Contract(TOKEN_ADDRESS, ERC20Abi, signer);
      const stakingV2 = new ethers.Contract(STAKING_V2_ADDRESS, stakingAbi, signer);

      if (allowanceV2Wei.lt(amtWei)) {
        const approveMsg = `Approving ${ethers.utils.formatUnits(amtWei, DECIMALS)} ECY for staking v2...`;
        setStatus(`ℹ️ ${approveMsg}`);
        showInfoToast('Token Approval', 'Please approve the token spending in your wallet...');
        
        const txA = await token.approve(STAKING_V2_ADDRESS, amtWei);
        showInfoToast('Approval Sent', 'Token approval transaction sent. Waiting for confirmation...');
        await txA.wait();
        
        setStatus("✅ Approval confirmed. Sending stake to v2...");
        showSuccessToast('Approval Confirmed', 'Token spending approved successfully.');

        const addr = await signer.getAddress();
        const newAllowanceV2: ethers.BigNumber = await token.allowance(addr, STAKING_V2_ADDRESS);
        setAllowanceV2Wei(newAllowanceV2);
        setAllowanceV2(weiToStr(newAllowanceV2));
      } else {
        setStatus("Allowance OK. Sending stake to v2...");
        showInfoToast('Staking', 'Sending staking transaction...');
      }

      const tx = await stakingV2.enterStaking(amtWei);
      setStatus("Stake (v2) transaction sent: " + tx.hash);
      showInfoToast('Transaction Sent', `Staking transaction sent: ${tx.hash.slice(0, 10)}...`);
      
      await tx.wait();
      await refreshAll(account, signer);
      setStatus("✅ Staked successfully on v2!");
      showSuccessToast('Staking Successful', `Successfully staked ${ethers.utils.formatUnits(amtWei, DECIMALS)} ECY tokens!`);
      setAmount("");
    } catch (err: any) {
      const errorMsg = err?.message || String(err);
      setStatus("❌ Stake error: " + errorMsg);
      showErrorToast('Staking Failed', errorMsg);
    } finally {
      setIsStaking(false);
    }
  };

  // Unstake from V1
  const unstakeFromV1 = async () => {
    if (!signer || !account) return;
    const stakingV1 = new ethers.Contract(STAKING_V1_ADDRESS, stakingAbi, signer);
    try {
      setIsUnstaking(true);
      const amtWei = getClampedAmountFor("unstakeV1");
      if (amtWei.lte(ZERO)) {
        const errorMsg = "Amount must be > 0 and ≤ staked v1 balance.";
        setStatus("❌ " + errorMsg);
        showErrorToast('Invalid Amount', errorMsg);
        return;
      }
      
      showInfoToast('Unstaking V1', 'Sending unstaking transaction for v1...');
      const tx = await stakingV1.leaveStaking(amtWei);
      setStatus("Unstake (v1) transaction sent: " + tx.hash);
      showInfoToast('Transaction Sent', `Unstaking transaction sent: ${tx.hash.slice(0, 10)}...`);
      
      await tx.wait();
      setStatus("✅ Unstaked from v1 successfully!");
      showSuccessToast('Unstaking Successful', `Successfully unstaked ${ethers.utils.formatUnits(amtWei, DECIMALS)} ECY from v1!`);
      await refreshAll(account, signer);
      setAmount("");
    } catch (err: any) {
      const errorMsg = err?.message || String(err);
      setStatus("❌ Unstake v1 error: " + errorMsg);
      showErrorToast('Unstaking Failed', errorMsg);
    } finally {
      setIsUnstaking(false);
    }
  };

  // Unstake from V2
  const unstakeFromV2 = async () => {
    if (!signer || !account) return;
    const stakingV2 = new ethers.Contract(STAKING_V2_ADDRESS, stakingAbi, signer);
    try {
      setIsUnstaking(true);
      const amtWei = getClampedAmountFor("unstakeV2");
      if (amtWei.lte(ZERO)) {
        const errorMsg = "Amount must be > 0 and ≤ staked v2 balance.";
        setStatus("❌ " + errorMsg);
        showErrorToast('Invalid Amount', errorMsg);
        return;
      }
      
      showInfoToast('Unstaking V2', 'Sending unstaking transaction for v2...');
      const tx = await stakingV2.leaveStaking(amtWei);
      setStatus("Unstake (v2) transaction sent: " + tx.hash);
      showInfoToast('Transaction Sent', `Unstaking transaction sent: ${tx.hash.slice(0, 10)}...`);
      
      await tx.wait();
      setStatus("✅ Unstaked from v2 successfully!");
      showSuccessToast('Unstaking Successful', `Successfully unstaked ${ethers.utils.formatUnits(amtWei, DECIMALS)} ECY from v2!`);
      await refreshAll(account, signer);
      setAmount("");
    } catch (err: any) {
      const errorMsg = err?.message || String(err);
      setStatus("❌ Unstake v2 error: " + errorMsg);
      showErrorToast('Unstaking Failed', errorMsg);
    } finally {
      setIsUnstaking(false);
    }
  };

  // Set max amounts
  const setMaxWallet = () => {
    setAmount(tokenBalance);
    showInfoToast('Max Amount Set', 'Set to maximum wallet balance.');
  };
  
  const setMaxStakedV1 = () => {
    setAmount(stakedV1);
    showInfoToast('Max Amount Set', 'Set to maximum staked v1 balance.');
  };
  
  const setMaxStakedV2 = () => {
    setAmount(stakedV2);
    showInfoToast('Max Amount Set', 'Set to maximum staked v2 balance.');
  };

  // Save wallet setup
  const saveWalletSetup = async (xch: string, fil: string, ar: string) => {
    if (!jwt) throw new Error("JWT token is required");
    
    showInfoToast('Saving Wallets', 'Updating wallet configuration...');
    
    const r = await fetch(`${API_BASE}/users/me`, {
      method: "PATCH",
      headers: { 
        "Content-Type": "application/json", 
        Authorization: `Bearer ${jwt}` 
      },
      body: JSON.stringify({
        email: profile.email ?? undefined,
        xch_wallet: profile.xch_wallet ? undefined : xch,
        fil_wallet: profile.fil_wallet ? undefined : fil,
        ar_wallet: profile.ar_wallet ? undefined : ar,
      }),
    });
    
    const data = await r.json();
    if (!r.ok) {
      throw new Error(friendlyApiError(data?.error, r.statusText || "Save error"));
    }
    
    await loadMe(jwt);
    setAuthStatus("✅ Wallets saved. Staking is now active.");
    showSuccessToast('Wallets Configured', 'Wallet setup completed successfully. Staking is now available!');
  };

  // Show loading state while checking connection
  if (isInitializing) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-black">
        <AnimatedBackground backgroundImage="/webp1.webp" />
        <div className="relative min-h-screen flex items-center justify-center">
          <div className="bg-black/90 p-8 rounded-2xl border border-white/10">
            <div className="flex flex-col items-center space-y-6" role="status" aria-busy="true">
              <div className="relative">
                <div
                  className="w-20 h-20 border-4 border-gradient-to-r from-emerald-400 to-cyan-400 border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                />
                <div className="absolute inset-0 w-20 h-20 border-2 border-emerald-400/20 rounded-full animate-pulse" />
              </div>
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text">
                  Initializing EcoWay Staking
                </h2>
                <p className="text-gray-300 font-medium">Checking wallet connection...</p>
                <p className="text-sm text-gray-400">Please wait while we prepare your staking dashboard</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <AnimatedBackground ambientCount={160} />
      
      <div className="relative min-h-screen bg-black/20 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img
                src="https://ecoway.org/static/media/white.84a1607b77c7d18edce26b793502bdbd.svg"
                alt="EcoWay logo"
                className="h-12 md:h-16 drop-shadow-lg"
                draggable={false}
              />
              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight
                         bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent
                         drop-shadow-[0_2px_10px_rgba(16,185,129,0.35)]">
                <span className="text-cyan-200">$ECY</span> Staking Platform
              </h1>
            </div>

            <p className="text-white/80 text-lg font-medium">
              Stake <span className="text-cyan-100 font-extrabold">$ECY</span> tokens and earn rewards
            </p>
            <p className="text-white/60 text-sm mt-1">
              Secure, decentralized staking on Binance Smart Chain
            </p>
          </div>

          {!account ? (
            <div className="relative p-8 rounded-2xl
                           bg-black/90 backdrop-blur-xl
                           border border-white/20 shadow-2xl
                           transform hover:scale-[1.02]
                           transition-all duration-300
                           before:content-[''] before:absolute before:-inset-px before:rounded-2xl
                           before:bg-gradient-to-r before:from-emerald-400/10 before:to-cyan-400/10
                           before:blur-xl before:opacity-50 before:pointer-events-none">
              <div className="text-center mb-8">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 
                              rounded-full flex items-center justify-center shadow-lg border border-white/10">
                  <img
                    src="https://c.tenor.com/ewkgtBsOGtUAAAAC/tenor.gif"
                    alt="Loading animation"
                    className="w-16 h-16 object-contain select-none pointer-events-none rounded-full"
                    draggable={false}
                  />
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight
                             bg-gradient-to-r from-emerald-100 to-cyan-400
                             bg-clip-text text-transparent
                             drop-shadow-[0_1px_10px_rgba(16,185,129,0.35)] mb-3">
                  Welcome to EcoWay Staking Platform 2.0
                </h2>
                <p className="text-emerald-200/90 text-lg mb-2">
                  Connect your MetaMask wallet to get started
                </p>
                <p className="text-white/60 text-sm">
                  Earn rewards by staking your $ECY tokens securely
                </p>
              </div>
              <WalletConnect 
                account={account}
                isConnecting={isConnecting}
                onConnect={connectWallet}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {!allRewardWalletsPresent && (
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm 
                              border border-yellow-500/30 rounded-xl p-4 shadow-lg">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-yellow-100 mb-1">
                        Wallet Configuration Required
                      </h3>
                      <p className="text-yellow-200/90 text-sm mb-3">
                        You need to configure your XCH, FIL and AR wallet addresses before you can use staking features.
                      </p>
                      <button
                        onClick={() => setShowWalletSetup(true)}
                        className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 
                                 hover:from-yellow-600 hover:to-orange-600
                                 text-white font-bold rounded-lg shadow-lg
                                 transition-all duration-200 transform hover:scale-105
                                 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-black/50"
                      >
                        Configure Wallets Now
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="backdrop-blur-sm rounded-xl p-1 shadow-lg border border-white/20">
                <WalletConnect 
                  account={account}
                  isConnecting={isConnecting}
                  onConnect={connectWallet}
                />
              </div>

              {jwt && (
                <StakingReactivationCard
                  status={stakingActivation}
                  loading={isLoadingActivation}
                  processing={isReactivating}
                  onReactivate={reactivateStaking}
                />
              )}

              <div className="bg-black/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 overflow-hidden">
                <div className="flex border-b border-white/10">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex-1 px-6 py-4 font-bold text-sm transition-all duration-200
                              ${activeTab === 'overview'
                                ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-100 border-b-2 border-emerald-400'
                                : 'text-white/60 hover:text-white/80 hover:bg-white/5'
                              }`}
                  >
                    <div className="flex items-center justify-center gap-2">
👤
                      Account Overview
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('staking')}
                    className={`flex-1 px-6 py-4 font-bold text-sm transition-all duration-200
                              ${activeTab === 'staking'
                                ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-100 border-b-2 border-emerald-400'
                                : 'text-white/60 hover:text-white/80 hover:bg-white/5'
                              }`}
                  >
                    <div className="flex items-center justify-center gap-2">
⚙️
                      Staking Dashboard
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('earnings')}
                    className={`flex-1 px-6 py-4 font-bold text-sm transition-all duration-200
                              ${activeTab === 'earnings'
                                ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-100 border-b-2 border-emerald-400'
                                : 'text-white/60 hover:text-white/80 hover:bg-white/5'
                              }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      💰
                      Earnings
                    </div>
                  </button>
                </div>

                <div className="transition-all duration-300">
                  {activeTab === 'overview' ? (
                    <AuthSection
                      jwt={jwt}
                      profile={profile}
                      transactions={transactions}
                      authStatus={authStatus}
                      isLoggingIn={isLoggingIn}
                      isSaving={isSaving}
                      onLogin={loginBackend}
                      onLogout={logoutBackend}
                      onReload={() => jwt && loadMe(jwt)}
                      onSave={saveMe}
                      onProfileChange={(updates) => setProfile(p => ({ ...p, ...updates }))}
                      onOpenWalletSetup={() => setShowWalletSetup(true)}
                    />
                  ) : activeTab === 'staking' ? (
                    <StakingPanel
                      tokenBalance={tokenBalance}
                      stakedV1={stakedV1}
                      stakedV2={stakedV2}
                      cooldownV1={cooldownV1}
                      cooldownV2={cooldownV2}
                      amount={amount}
                      onAmountChange={setAmount}
                      onStakeV2={stakeOnV2}
                      onUnstakeV1={unstakeFromV1}
                      onUnstakeV2={unstakeFromV2}
                      onSetMaxWallet={setMaxWallet}
                      onSetMaxV1={setMaxStakedV1}
                      onSetMaxV2={setMaxStakedV2}
                      isStaking={isStaking}
                      isUnstaking={isUnstaking}
                      status={status}
                      bothWalletsPresent={allRewardWalletsPresent}
                      onOpenWalletSetup={() => setShowWalletSetup(true)}
                      tokenBalanceWei={tokenBalanceWei}
                      stakedV1Wei={stakedV1Wei}
                      stakedV2Wei={stakedV2Wei}
                      transactions={transactions}
                    />
                  ) : (
                    <EarningsPanel
                      transactions={transactions}
                      jwt={jwt}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          <WalletSetupModal
            show={showWalletSetup}
            profile={profile}
            jwt={jwt}
            onClose={() => setShowWalletSetup(false)}
            onSave={saveWalletSetup}
          />
        </div>
      </div>

      {account && !jwt && (
        <AuthModal
          isOpen={true}
          onClose={() => {}}
          jwt={jwt}
          profile={profile}
          transactions={transactions}
          authStatus={authStatus}
          isLoggingIn={isLoggingIn}
          isSaving={isSaving}
          onLogin={loginBackend}
          onLogout={logoutBackend}
          onReload={() => jwt && loadMe(jwt)}
          onSave={saveMe}
          onProfileChange={(updates) => setProfile(p => ({ ...p, ...updates }))}
          onOpenWalletSetup={() => setShowWalletSetup(true)}
        />
      )}

      <div className="fixed bottom-4 right-4 z-[99999] space-y-2 max-w-sm pointer-events-none">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
